import { economyConfig } from '../config/economyConfig';
import { simConfig } from '../config/gameConfig';
import { SPAWNABLE_TYPES, VEHICLE_TYPES } from '../data/vehicles';
import type { GameTime } from '../models/GameTime';
import type { ParkingLot } from '../models/ParkingLot';
import { CAPACITY_TILES, TILE } from '../models/Tile';
import {
  createVehicle,
  isOccupyingSpace,
  VEHICLE_STATE,
  type PathPoint,
  type Vehicle,
} from '../models/Vehicle';
import type { RandomGenerator } from '../utils/random';
import { clamp } from '../utils/math';
import {
  bfsFrom,
  navigableNeighborsOf,
  reconstruct,
  tileToPoint,
} from '../utils/pathfinding';
import { DemandEngine } from './DemandEngine';
import { EconomyEngine } from './EconomyEngine';
import { StaffEngine } from './StaffEngine';
import { UpgradeEngine } from './UpgradeEngine';

/**
 * VehicleEngine (spec sections 9 & 29): drives the vehicle lifecycle for a lot.
 *
 * Pure logic over the lot's plain data — no Phaser. Per fixed tick it spawns
 * cars at a fixed cadence (Phase 5's DemandEngine will replace this), advances
 * each vehicle through its state machine, and keeps occupancy/metrics in sync.
 *
 * Lifecycle: entering → searching → driving-to-space → parked →
 * exiting → paying → left. If no space is reachable within patience: abandoned.
 */
export const VehicleEngine = {
  /**
   * Advance all vehicles in a lot by `dtMinutes`, then maybe spawn.
   * Returns the parking revenue collected this tick (from cars that paid), so
   * the caller can credit the player's cash / P&L.
   */
  update(
    lot: ParkingLot,
    dtMinutes: number,
    time: GameTime,
    rng: RandomGenerator,
    competitionMult = 1
  ): number {
    let revenue = 0;
    for (const v of lot.vehicles) {
      revenue += this.updateVehicle(lot, v, dtMinutes, rng);
    }

    // Process the exit payment queue (limited lanes -> queues form under load).
    revenue += this.processPaymentQueue(lot, dtMinutes);

    // Remove vehicles that have left the lot entirely.
    if (lot.vehicles.some((v) => v.state === VEHICLE_STATE.Left)) {
      lot.vehicles = lot.vehicles.filter((v) => v.state !== VEHICLE_STATE.Left);
    }

    this.trySpawn(lot, dtMinutes, time, rng, competitionMult);
    this.syncOccupancy(lot);
    return revenue;
  },

  syncOccupancy(lot: ParkingLot): void {
    let occupied = 0;
    for (const v of lot.vehicles) if (isOccupyingSpace(v)) occupied++;
    lot.occupiedSpaces = occupied;
  },

  /** Update the rolling average wait time (EMA) with a new sample. */
  recordWait(lot: ParkingLot, waitMinutes: number): void {
    const a = economyConfig.REPUTATION.EMA_ALPHA;
    lot.avgWaitMinutes = lot.avgWaitMinutes * (1 - a) + waitMinutes * a;
  },

  /** Update the rolling rejection rate (EMA); sample is 0 (served) or 1 (rejected). */
  recordRejection(lot: ParkingLot, sample: 0 | 1): void {
    const a = economyConfig.REPUTATION.EMA_ALPHA;
    lot.rejectionRateEma = lot.rejectionRateEma * (1 - a) + sample * a;
  },

  // --- spawning ---

  trySpawn(
    lot: ParkingLot,
    dtMinutes: number,
    time: GameTime,
    rng: RandomGenerator,
    competitionMult = 1
  ): void {
    const entrances = this.tilesOfType(lot, TILE.Entrance);
    const hasExit = this.tilesOfType(lot, TILE.Exit).length > 0;
    const capacity = this.capacityTiles(lot).length;

    // Only spawn where cars can actually enter, leave, and park.
    if (entrances.length === 0 || !hasExit || capacity === 0) return;

    // Arrivals: DemandEngine (price/time/day/reputation) × market competition.
    const expected = DemandEngine.expectedArrivals(
      lot,
      time,
      dtMinutes,
      competitionMult
    );
    const count = DemandEngine.sampleArrivals(expected, rng);
    if (count <= 0) return;

    // Entry gate: a car can only get a ticket if the lot has an automatic
    // barrier system (barrier / LPR) or a cashier at the gate to hand one out.
    // Without either, arriving cars can't enter and are turned away — counted
    // as rejected so it hurts availability & reputation.
    if (!this.canAdmitEntry(lot)) {
      for (let i = 0; i < count; i++) {
        lot.vehiclesRejected++;
        this.recordRejection(lot, 1);
      }
      return;
    }

    for (let i = 0; i < count; i++) {
      if (lot.vehicles.length >= simConfig.maxVehiclesPerLot) break;
      this.spawnVehicle(lot, entrances, time.totalMinutes, rng);
    }
  },

  /**
   * Whether cars can get a ticket at the gate: an automatic barrier system
   * (barrier / LPR upgrade) or a cashier on staff to hand tickets out.
   */
  canAdmitEntry(lot: ParkingLot): boolean {
    if (StaffEngine.counts(lot).cashier > 0) return true; // an attendant
    if (UpgradeEngine.aggregate(lot).entryAutomation) return true; // barrier/LPR
    return false;
  },

  spawnVehicle(
    lot: ParkingLot,
    entrances: number[],
    minute: number,
    rng: RandomGenerator
  ): Vehicle {
    const entrance = entrances[rng.nextInt(0, entrances.length - 1)];
    const point = tileToPoint(lot, entrance);
    const evEnabled = UpgradeEngine.aggregate(lot).evEnabled;
    const typeId = this.pickType(rng, evEnabled);
    const def = VEHICLE_TYPES[typeId];
    const duration = rng.nextInt(
      def.minDurationMinutes,
      def.maxDurationMinutes
    );

    const vehicle = createVehicle({
      id: `${lot.id}-v${lot.vehicleSeq++}`,
      typeId,
      x: point.x,
      y: point.y,
      speed: simConfig.vehicleSpeedTilesPerMinute,
      spawnedAtMinute: minute,
      plannedDurationMinutes: duration,
    });
    lot.vehicles.push(vehicle);
    return vehicle;
  },

  pickType(rng: RandomGenerator, evEnabled = false): string {
    // EV drivers only appear once the lot has an EV charger (spec section 10).
    const pool = evEnabled ? [...SPAWNABLE_TYPES, 'ev'] : SPAWNABLE_TYPES;
    let total = 0;
    for (const id of pool) total += VEHICLE_TYPES[id].spawnWeight;
    let roll = rng.nextRange(0, total);
    for (const id of pool) {
      roll -= VEHICLE_TYPES[id].spawnWeight;
      if (roll <= 0) return id;
    }
    return pool[0];
  },

  // --- per-vehicle state machine ---

  updateVehicle(
    lot: ParkingLot,
    v: Vehicle,
    dt: number,
    _rng: RandomGenerator
  ): number {
    switch (v.state) {
      case VEHICLE_STATE.Entering:
        v.state = VEHICLE_STATE.Searching;
        this.trySearch(lot, v, dt);
        break;

      case VEHICLE_STATE.Searching:
        this.trySearch(lot, v, dt);
        break;

      case VEHICLE_STATE.DrivingToSpace:
        if (this.moveAlong(v, dt)) {
          v.state = VEHICLE_STATE.Parked;
          v.parkedRemainingMinutes = v.plannedDurationMinutes;
          // The car found a space: record its wait and a "not rejected" sample.
          this.recordWait(lot, v.searchElapsedMinutes);
          this.recordRejection(lot, 0);
        }
        break;

      case VEHICLE_STATE.Parked:
        v.parkedRemainingMinutes -= dt;
        if (v.parkedRemainingMinutes <= 0) this.beginLeaving(lot, v);
        break;

      case VEHICLE_STATE.Exiting:
        if (this.moveAlong(v, dt)) {
          if (v.paid) {
            // Nothing to pay (no collector) — drive straight out.
            v.state = VEHICLE_STATE.Left;
          } else {
            // Reached the exit: join THAT exit's payment queue (spec §15).
            v.state = VEHICLE_STATE.Paying;
            v.enteredQueueAt = lot.paymentSeq++;
            v.queueExitIndex = this.nearestExitIndex(lot, v.x, v.y);
            v.payingRemainingMinutes = simConfig.payingMinutesBase;
            v.queueWaitMinutes = 0;
          }
        }
        break;

      case VEHICLE_STATE.Paying:
        // Handled collectively in processPaymentQueue (queue + limited lanes).
        break;

      case VEHICLE_STATE.Abandoned:
        if (this.moveAlong(v, dt)) {
          v.state = VEHICLE_STATE.Left; // already counted as rejected
        }
        break;

      case VEHICLE_STATE.Left:
        break;
    }
    return 0;
  },

  /** Try to reserve a space and head for it; else keep searching / abandon. */
  trySearch(lot: ParkingLot, v: Vehicle, dt: number): void {
    if (this.assignSpace(lot, v)) {
      v.state = VEHICLE_STATE.DrivingToSpace;
      return;
    }
    v.searchElapsedMinutes += dt;
    if (v.searchElapsedMinutes >= simConfig.searchPatienceMinutes) {
      this.abandon(lot, v);
    }
  },

  beginLeaving(lot: ParkingLot, v: Vehicle): void {
    const spaceIndex = v.targetSpace;
    v.targetSpace = null; // frees the space immediately
    lot.vehiclesServed++; // the car parked and is now leaving

    // A fee can only be collected if there's someone/something to collect it:
    // a Cashier (staff), a Payment Machine tile, or an automated upgrade.
    // Without any, the car leaves WITHOUT paying (spec section 14 — staff matter).
    const canCollect = this.canCollectPayment(lot);
    v.paid = !canCollect;

    if (spaceIndex === null) {
      v.state = VEHICLE_STATE.Left;
      return;
    }

    // Drive (in-car) to the exit; payment happens there, in a queue.
    const path = this.pathToExit(lot, spaceIndex);
    if (path) {
      v.path = path;
      v.pathIndex = 0;
      v.state = VEHICLE_STATE.Exiting;
    } else {
      v.state = VEHICLE_STATE.Left;
    }
  },

  /** Whether this lot has any means to collect a parking fee. */
  canCollectPayment(lot: ParkingLot): boolean {
    if (StaffEngine.counts(lot).cashier > 0) return true; // a person
    if (this.tilesOfType(lot, TILE.PaymentMachine).length > 0) return true; // machine
    if (UpgradeEngine.aggregate(lot).paymentThroughput > 0) return true; // barrier/QR/LPR
    return false;
  },

  abandon(lot: ParkingLot, v: Vehicle): void {
    lot.vehiclesRejected++;
    this.recordRejection(lot, 1); // a rejected arrival
    const startIndex = this.tileIndexOf(lot, v.x, v.y);
    const path = this.pathToExit(lot, startIndex);
    if (path) {
      v.path = path;
      v.pathIndex = 0;
      v.state = VEHICLE_STATE.Abandoned;
    } else {
      v.state = VEHICLE_STATE.Left;
    }
  },

  // --- movement ---

  /** Move along the current path; returns true when the end is reached. */
  moveAlong(v: Vehicle, dt: number): boolean {
    let budget = v.speed * dt;
    const eps = 1e-6;
    while (budget > eps && v.pathIndex < v.path.length) {
      const target = v.path[v.pathIndex];
      const dx = target.x - v.x;
      const dy = target.y - v.y;
      const d = Math.hypot(dx, dy);
      if (d <= budget + eps) {
        v.x = target.x;
        v.y = target.y;
        budget -= d;
        v.pathIndex++;
      } else {
        v.x += (dx / d) * budget;
        v.y += (dy / d) * budget;
        budget = 0;
      }
    }
    return v.pathIndex >= v.path.length;
  },

  // --- pathing / space selection ---

  /**
   * Reserve the nearest reachable free space and set a path to it (ending with
   * a pull-in to the space center). Returns false if none is reachable.
   */
  assignSpace(lot: ParkingLot, v: Vehicle): boolean {
    const free = this.freeSpaces(lot);
    if (free.length === 0) return false;

    // Map each free space's navigable approach tiles back to the space.
    const approachToSpace = new Map<number, number>();
    for (const space of free) {
      for (const nb of navigableNeighborsOf(lot, space)) {
        if (!approachToSpace.has(nb)) approachToSpace.set(nb, space);
      }
    }
    if (approachToSpace.size === 0) return false;

    const start = this.tileIndexOf(lot, v.x, v.y);
    const { dist, prev } = bfsFrom(lot, [start]);

    let bestApproach = -1;
    let bestDist = Infinity;
    for (const [approach] of approachToSpace) {
      const d = dist.get(approach);
      if (d !== undefined && d < bestDist) {
        bestDist = d;
        bestApproach = approach;
      }
    }
    if (bestApproach < 0) return false;

    const space = approachToSpace.get(bestApproach)!;
    const tilePath = reconstruct(prev, bestApproach);
    const points = tilePath.map((i) => tileToPoint(lot, i));
    points.push(tileToPoint(lot, space)); // pull into the space

    v.path = points;
    v.pathIndex = 0;
    v.targetSpace = space;
    return true;
  },

  /** Path (points) from a tile to the nearest reachable exit, or null. */
  pathToExit(lot: ParkingLot, fromIndex: number): PathPoint[] | null {
    const exits = this.tilesOfType(lot, TILE.Exit);
    if (exits.length === 0) return null;

    const { dist, prev } = bfsFrom(lot, [fromIndex]);
    let bestExit = -1;
    let bestDist = Infinity;
    for (const exit of exits) {
      const d = dist.get(exit);
      if (d !== undefined && d < bestDist) {
        bestDist = d;
        bestExit = exit;
      }
    }
    if (bestExit < 0) return null;

    const tilePath = reconstruct(prev, bestExit);
    return tilePath.map((i) => tileToPoint(lot, i));
  },

  /**
   * Path (points) to the navigable tile beside the nearest cashier / payment
   * machine, so the car stops there to pay. Null if there is none reachable.
   */
  pathToPayment(lot: ParkingLot, fromIndex: number): PathPoint[] | null {
    const booths = [
      ...this.tilesOfType(lot, TILE.Cashier),
      ...this.tilesOfType(lot, TILE.PaymentMachine),
    ];
    if (booths.length === 0) return null;

    const approaches = new Map<number, true>();
    for (const b of booths) {
      for (const nb of navigableNeighborsOf(lot, b)) approaches.set(nb, true);
    }
    if (approaches.size === 0) return null;

    const { dist, prev } = bfsFrom(lot, [fromIndex]);
    let best = -1;
    let bestDist = Infinity;
    for (const a of approaches.keys()) {
      const d = dist.get(a);
      if (d !== undefined && d < bestDist) {
        bestDist = d;
        best = a;
      }
    }
    if (best < 0) return null;
    return reconstruct(prev, best).map((i) => tileToPoint(lot, i));
  },

  isOnExit(lot: ParkingLot, v: Vehicle): boolean {
    return this.getTileAt(lot, v.x, v.y) === TILE.Exit;
  },

  getTileAt(lot: ParkingLot, x: number, y: number): string {
    const tx = Math.round(x);
    const ty = Math.round(y);
    if (tx < 0 || ty < 0 || tx >= lot.width || ty >= lot.height) return TILE.Empty;
    return lot.tiles[ty * lot.width + tx];
  },

  // --- payment queue (spec section 15) ---

  /** Parallel payment lanes: more cashiers / machines / automation = shorter queues. */
  paymentLanes(lot: ParkingLot): number {
    const cashiers = StaffEngine.counts(lot).cashier;
    const machines = this.tilesOfType(lot, TILE.PaymentMachine).length;
    const automation = UpgradeEngine.aggregate(lot).paymentThroughput > 0 ? 2 : 0;
    return clamp(cashiers + machines + automation, 1, 6);
  },

  /**
   * Advance the exit payment queue. Up to `lanes` cars are served in parallel;
   * the rest wait (accruing queue time, which hurts satisfaction). Cars are
   * positioned in a line leading back from the exit so the queue is visible.
   * Returns fees collected this tick.
   */
  processPaymentQueue(lot: ParkingLot, dt: number): number {
    const paying = lot.vehicles.filter((v) => v.state === VEHICLE_STATE.Paying);
    if (paying.length === 0) return 0;

    // Group cars by the exit they queued at, so each gate has its own line.
    const byExit = new Map<number, Vehicle[]>();
    for (const v of paying) {
      const key = v.queueExitIndex;
      const arr = byExit.get(key);
      if (arr) arr.push(v);
      else byExit.set(key, [v]);
    }

    const lanes = this.paymentLanes(lot); // per gate
    let revenue = 0;

    for (const [exitIndex, group] of byExit) {
      group.sort((a, b) => a.enteredQueueAt - b.enteredQueueAt);
      const geo = this.exitQueueGeometry(lot, exitIndex);
      group.forEach((v, i) => {
        // Line the cars up behind their own exit so the queue is visible.
        v.x = geo.x + geo.dx * i;
        v.y = geo.y + geo.dy * i;

        if (i < lanes) {
          v.payingRemainingMinutes -= dt;
          if (v.payingRemainingMinutes <= 0) {
            v.paid = true;
            const fee = EconomyEngine.computeParkingFee(lot, v);
            lot.revenue += fee;
            revenue += fee;
            this.recordWait(lot, v.queueWaitMinutes);
            v.state = VEHICLE_STATE.Left;
          }
        } else {
          v.queueWaitMinutes += dt;
        }
      });
    }

    return revenue;
  },

  /** Index of the exit tile nearest to (x, y), or -1 if there are none. */
  nearestExitIndex(lot: ParkingLot, x: number, y: number): number {
    const exits = this.tilesOfType(lot, TILE.Exit);
    let best = -1;
    let bestD = Infinity;
    for (const e of exits) {
      const ex = e % lot.width;
      const ey = Math.floor(e / lot.width);
      const d = Math.hypot(ex - x, ey - y);
      if (d < bestD) {
        bestD = d;
        best = e;
      }
    }
    return best;
  },

  /** Exit tile position and the unit step pointing back into the lot. */
  exitQueueGeometry(
    lot: ParkingLot,
    exitIndex: number
  ): { x: number; y: number; dx: number; dy: number } {
    if (exitIndex < 0 || exitIndex >= lot.tiles.length) {
      return { x: 0, y: 0, dx: 0, dy: -1 };
    }
    const ex = exitIndex % lot.width;
    const ey = Math.floor(exitIndex / lot.width);
    const neighbors = navigableNeighborsOf(lot, exitIndex);
    if (neighbors.length > 0) {
      const n = neighbors[0];
      return { x: ex, y: ey, dx: (n % lot.width) - ex, dy: Math.floor(n / lot.width) - ey };
    }
    return { x: ex, y: ey, dx: 0, dy: -1 };
  },

  // --- helpers ---

  payingMinutes(lot: ParkingLot): number {
    // Cashiers (staff) and a cashier booth speed up payment throughput.
    const factor = StaffEngine.paymentTimeFactor(lot);
    return Math.max(0.2, simConfig.payingMinutesBase * factor);
  },

  tileIndexOf(lot: ParkingLot, x: number, y: number): number {
    const tx = Math.round(x);
    const ty = Math.round(y);
    return ty * lot.width + tx;
  },

  tilesOfType(lot: ParkingLot, type: (typeof TILE)[keyof typeof TILE]): number[] {
    const result: number[] = [];
    for (let i = 0; i < lot.tiles.length; i++) {
      if (lot.tiles[i] === type) result.push(i);
    }
    return result;
  },

  capacityTiles(lot: ParkingLot): number[] {
    const result: number[] = [];
    for (let i = 0; i < lot.tiles.length; i++) {
      if (CAPACITY_TILES.has(lot.tiles[i])) result.push(i);
    }
    return result;
  },

  freeSpaces(lot: ParkingLot): number[] {
    const taken = new Set<number>();
    for (const v of lot.vehicles) {
      if (v.targetSpace !== null && isOccupyingSpace(v)) taken.add(v.targetSpace);
    }
    return this.capacityTiles(lot).filter((i) => !taken.has(i));
  },
};
